import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getGroupById, getGroupPosts, joinGroup, leaveGroup, createGroupPost, deleteGroupPost, toggleGroupPostLike } from '../../services/firestoreService';
import { notifyNewGroupPost } from '../../services/notificationService';
import { handleError, handleSuccess, handleWarning } from '../../services/errorHandlingService';
import GroupAdmin from '../../components/groups/GroupAdmin';
import GroupComments from '../../components/groups/GroupComments';
import { SEED_GROUPS } from '../../data/seedGroups';
import { getSeedDiscussions } from '../../data/seedDiscussions';
import { resolveGroupImage } from '../../utils/groupImage';

const GroupDetail = () => {
  const { id } = useParams();
  const { currentUser, hasRole } = useAuth();
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMember, setIsMember] = useState(false);

  // New discussion state
  const [showNewDiscussion, setShowNewDiscussion] = useState(false);
  const [newDiscussion, setNewDiscussion] = useState({
    title: '',
    content: ''
  });
  const [submittingDiscussion, setSubmittingDiscussion] = useState(false);

  // Check if user is logged in
  const isLoggedIn = !!currentUser;

  useEffect(() => {
    console.log('GroupDetail component mounted for group ID:', id);
    loadGroupData();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGroupData = async () => {
    try {
      setLoading(true);

      // Demo/seed group? Serve it from local seed data (it isn't in Firestore).
      if (typeof id === 'string' && id.startsWith('seed-group-')) {
        const seedGroup = SEED_GROUPS.find((g) => g.id === id);
        if (!seedGroup) { setGroup(null); setLoading(false); return; }
        setGroup({ ...seedGroup, members: [], memberCount: seedGroup.members, isSeed: true });
        setPosts(getSeedDiscussions(id));
        setIsAdmin(hasRole('admin'));
        setIsMember(false);
        setLoading(false);
        return;
      }

      // Load group data
      const groupData = await getGroupById(id);
      console.log('Group data loaded:', groupData);
      if (!groupData) {
        console.log('No group data found for ID:', id);
        setGroup(null);
        setLoading(false);
        return;
      }

      setGroup(groupData);

      // Load group posts
      const groupPosts = await getGroupPosts(id);
      setPosts(groupPosts);

      // Check if user is admin or member
      if (currentUser) {
        const userIsAdmin = hasRole('admin') || groupData.moderators?.includes(currentUser.uid);
        const userIsMember = groupData.members?.includes(currentUser.uid);

        console.log('🔍 User permissions check:', {
          userId: currentUser.uid,
          email: currentUser.email,
          hasAdminRole: hasRole('admin'),
          groupModerators: groupData.moderators,
          groupMembers: groupData.members,
          userIsAdmin,
          userIsMember,
          groupId: groupData.id
        });

        setIsAdmin(userIsAdmin);
        setIsMember(userIsMember);

        // Additional logging for debugging
        console.log('🎯 Final state set:', {
          isAdmin: userIsAdmin,
          isMember: userIsMember
        });
      } else {
        console.log('❌ No current user found');
        setIsAdmin(false);
        setIsMember(false);
      }

    } catch (error) {
      console.error('Error loading group data:', error);
      setGroup(null);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGroup = async () => {
    if (!currentUser || !group) return;
    if (group.isSeed) { handleWarning('This is a demo group. You can join real groups as they’re created.'); return; }

    try {
      console.log('🔄 Joining group:', group.id, 'for user:', currentUser.uid);
      await joinGroup(group.id, currentUser.uid);

      // Update local state immediately for better UX
      setIsMember(true);
      setGroup(prev => ({
        ...prev,
        members: [...(prev.members || []), currentUser.uid],
        memberCount: (prev.memberCount || 0) + 1
      }));

      // Reload group data to get updated membership info from server
      await loadGroupData();

      handleSuccess('Successfully joined the group!');
    } catch (error) {
      console.error('Error joining group:', error);
      // Revert local state if server update failed
      setIsMember(false);
      setGroup(prev => ({
        ...prev,
        members: (prev.members || []).filter(id => id !== currentUser.uid),
        memberCount: Math.max(0, (prev.memberCount || 0) - 1)
      }));
      handleError(error, {
        context: {
          component: 'GroupDetail',
          action: 'joinGroup',
          groupId: group.id
        }
      });
    }
  };

  const handleLeaveGroup = async () => {
    if (!currentUser || !group) return;

    if (!window.confirm('Are you sure you want to leave this group?')) {
      return;
    }

    try {
      console.log('🔄 Leaving group:', group.id, 'for user:', currentUser.uid);

      // Update local state immediately for better UX
      setIsMember(false);
      setGroup(prev => ({
        ...prev,
        members: (prev.members || []).filter(id => id !== currentUser.uid),
        memberCount: Math.max(0, (prev.memberCount || 0) - 1)
      }));

      await leaveGroup(group.id, currentUser.uid);

      // Reload group data to get updated membership info from server
      await loadGroupData();
      handleSuccess('Successfully left the group.');
    } catch (error) {
      console.error('Error leaving group:', error);
      // Revert local state if server update failed
      setIsMember(true);
      setGroup(prev => ({
        ...prev,
        members: [...(prev.members || []), currentUser.uid],
        memberCount: (prev.memberCount || 0) + 1
      }));
      handleError(error, {
        context: {
          component: 'GroupDetail',
          action: 'leaveGroup',
          groupId: group.id
        }
      });
    }
  };

  // Handle new discussion creation - Only group managers can start discussions
  const handleNewDiscussion = () => {
    if (!isAdmin) {
      alert('Only group managers can start new discussions. Members can react and reply to existing discussions.');
      return;
    }
    setShowNewDiscussion(true);
    // Scroll to top when opening new discussion form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDiscussionSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser || !newDiscussion.title.trim() || !newDiscussion.content.trim()) {
      alert('Please fill in both title and content for your discussion.');
      return;
    }

    setSubmittingDiscussion(true);
    try {
      const postData = {
        groupId: group.id,
        title: newDiscussion.title.trim(),
        content: newDiscussion.content.trim(),
        authorId: currentUser.uid,
        authorName: currentUser.displayName || currentUser.email,
        type: 'discussion',
        likes: [],
        replies: []
      };

      await createGroupPost(postData, currentUser.uid);

      // Send notifications to group members
      try {
        await notifyNewGroupPost(
          group.id,
          newDiscussion.title,
          currentUser.displayName || currentUser.email,
          currentUser.uid
        );
      } catch (notificationError) {
        console.error('Error sending notifications:', notificationError);
        // Don't fail the post creation if notifications fail
      }

      // Reset form and close modal
      setNewDiscussion({ title: '', content: '' });
      setShowNewDiscussion(false);

      // Reload posts to show the new discussion
      const updatedPosts = await getGroupPosts(group.id);
      setPosts(updatedPosts);

      alert('Discussion created successfully!');
    } catch (error) {
      console.error('Error creating discussion:', error);
      alert('Error creating discussion. Please try again.');
    } finally {
      setSubmittingDiscussion(false);
    }
  };

  const handleCancelDiscussion = () => {
    setNewDiscussion({ title: '', content: '' });
    setShowNewDiscussion(false);
  };

  // Handle post deletion
  const handleDeletePost = async (postId) => {
    if (!isAdmin) {
      handleWarning('You do not have permission to delete posts.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      try {
        await deleteGroupPost(postId);
        // Reload posts to show updated list
        const updatedPosts = await getGroupPosts(group.id);
        setPosts(updatedPosts);
        handleSuccess('Post deleted successfully.');
      } catch (error) {
        handleError(error, {
          context: {
            component: 'GroupDetail',
            action: 'deletePost',
            postId,
            groupId: group.id
          }
        });
      }
    }
  };

  // Handle post like/unlike
  const handlePostLike = async (postId) => {
    if (!currentUser || !isMember) {
      handleWarning('You must be a group member to like posts.');
      return;
    }

    try {
      await toggleGroupPostLike(postId, currentUser.uid);
      // Reload posts to show updated like counts
      const updatedPosts = await getGroupPosts(group.id);
      setPosts(updatedPosts);
    } catch (error) {
      handleError(error, {
        context: {
          component: 'GroupDetail',
          action: 'togglePostLike',
          postId,
          groupId: group.id
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Group Not Found</h2>
          <p className="text-gray-600 mt-2">The group you're looking for doesn't exist.</p>
          <Link to="/groups" className="text-apn-blue hover:text-blue-700 mt-4 inline-block">
            ← Back to Groups
          </Link>
        </div>
      </div>
    );
  }

  // Format the group data for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Group Not Found</h1>
          <p className="text-gray-600 mb-8">The group you're looking for doesn't exist or has been removed.</p>
          <Link to="/groups" className="inline-block bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200">
            Back to Groups
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">{group.name}</h1>
          </div>
        </div>
      </div>

      {/* Group Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Group Header */}
            <div className="relative h-64 bg-white">
              {/* Group image */}
              <div className="h-full w-full overflow-hidden flex items-center justify-center bg-white">
                <img
                  src={resolveGroupImage(group.image)}
                  alt={group.name}
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                  }}
                />
              </div>
              <div className="absolute top-4 right-4 bg-gray-100 text-gray-700 px-3 py-1 rounded-md">
                {group.category}
              </div>
            </div>

            {/* Group Details */}
            <div className="p-6">
              <div className="flex flex-col md:flex-row justify-between mb-8">
                <div className="mb-4 md:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{group.name}</h2>
                  <p className="text-gray-600 mb-4">{group.description}</p>
                  <div className="flex space-x-4 text-sm text-gray-500">
                    <span>{group.memberCount || 0} members</span>
                    <span>{posts.length} posts</span>
                    <span>Created {formatDate(group.createdAt)}</span>
                  </div>
                </div>
                <div className="flex flex-col space-y-2">
                  {isAdmin && (
                    <Link
                      to={`/groups/${group.id}/admin`}
                      className="bg-gray-800 text-white py-2 px-6 rounded hover:bg-gray-700 transition duration-200"
                    >
                      Admin Controls
                    </Link>
                  )}
                  {isMember ? (
                    <button
                      onClick={handleLeaveGroup}
                      className="bg-red-600 text-white py-2 px-6 rounded hover:bg-red-700 transition duration-200"
                    >
                      Leave Group
                    </button>
                  ) : (
                    <button
                      onClick={handleJoinGroup}
                      className="bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200"
                    >
                      Join Group
                    </button>
                  )}
                </div>
              </div>

              {/* Admin Panel (only visible to admins) */}
              {isAdmin && (
                <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Controls</h3>
                  <GroupAdmin group={group} />
                </div>
              )}

              {/* Discussions */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Discussions</h3>
                    {isMember && !isAdmin && (
                      <p className="text-sm text-gray-500 mt-1">
                        Group managers start discussions. Members can react and reply to existing discussions.
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleNewDiscussion}
                    className={`py-1 px-4 rounded transition duration-200 ${
                      isAdmin
                        ? 'bg-apn-blue text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    disabled={!isAdmin}
                    title={!isAdmin ? 'Only group managers can start new discussions' : 'Start a new discussion'}
                  >
                    + New Discussion
                  </button>
                </div>

                <div className="space-y-6">
                  {posts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No discussions yet. Be the first to start a conversation!</p>
                    </div>
                  ) : (
                    posts.map(post => (
                    <div key={post.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4 bg-gray-50">
                        <div className="flex items-center mb-2">
                          <div className="h-10 w-10 rounded-full bg-apn-blue flex items-center justify-center text-white font-semibold mr-3">
                            {post.authorName ? post.authorName.charAt(0).toUpperCase() : 'U'}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{post.authorName || 'Unknown User'}</h4>
                            <p className="text-sm text-gray-500">Group Member</p>
                          </div>
                        </div>
                        <h3 className="text-xl font-bold mb-2">{post.title}</h3>
                        <p className="text-gray-700">{post.content}</p>
                        <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                          <span>{formatDate(post.createdAt)}</span>
                          <div className="flex items-center space-x-4">
                            <button
                              className={`flex items-center transition duration-200 ${
                                post.likes?.includes(currentUser?.uid)
                                  ? 'text-blue-600'
                                  : 'text-gray-500 hover:text-apn-blue'
                              } ${!isLoggedIn || !isMember ? 'opacity-50 cursor-not-allowed' : ''}`}
                              onClick={() => handlePostLike(post.id)}
                              disabled={!isLoggedIn || !isMember}
                            >
                              <svg className="h-5 w-5 mr-1" fill={post.likes?.includes(currentUser?.uid) ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.60L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                              </svg>
                              <span>{post.likeCount || post.likes?.length || 0}</span>
                            </button>
                            <button className="flex items-center hover:text-apn-blue">
                              <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                              </svg>
                              <span>{post.commentCount || post.replies?.length || 0}</span>
                            </button>
                            {isAdmin && (
                              <button
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDeletePost(post.id)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Comments Section */}
                      {post.isSeed ? (
                        <div className="border-t border-gray-100 px-4 py-3 space-y-3">
                          {(post.seedComments || []).map((c, i) => (
                            <div key={i} className="flex items-start">
                              <div className="h-8 w-8 rounded-full bg-apn-blue text-white flex items-center justify-center text-xs mr-3 flex-shrink-0">
                                {c.authorName ? c.authorName.charAt(0) : 'U'}
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">{c.authorName}</span>
                                <p className="text-sm text-gray-700">{c.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <GroupComments
                          post={post}
                          isLoggedIn={isLoggedIn}
                          isAdmin={isAdmin}
                          isMember={isMember}
                        />
                      )}
                    </div>
                  ))
                  )}
                </div>
              </div>

              {/* Members Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Members</h3>
                <div className="text-gray-600">
                  <p>This group has {group.memberCount || 0} members.</p>
                  {isMember ? (
                    <button
                      onClick={handleLeaveGroup}
                      className="mt-2 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition duration-200"
                    >
                      Leave Group
                    </button>
                  ) : (
                    <button
                      onClick={handleJoinGroup}
                      className="mt-2 bg-apn-blue text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                    >
                      Join Group
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Start Your Own Group</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Have a topic you're passionate about? Create a group and connect with like-minded professionals.
          </p>
          <Link to="/groups/create" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Create a Group
          </Link>
        </div>
      </section>

      {/* New Discussion Modal */}
      {showNewDiscussion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-full overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Start New Discussion</h3>
                <button
                  onClick={handleCancelDiscussion}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleDiscussionSubmit} className="space-y-4">
                <div>
                  <label htmlFor="discussionTitle" className="block text-sm font-medium text-gray-700 mb-2">
                    Discussion Title *
                  </label>
                  <input
                    type="text"
                    id="discussionTitle"
                    value={newDiscussion.title}
                    onChange={(e) => setNewDiscussion(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue focus:border-apn-blue"
                    placeholder="What would you like to discuss?"
                    required
                    maxLength={200}
                  />
                  <p className="text-sm text-gray-500 mt-1">{newDiscussion.title.length}/200 characters</p>
                </div>

                <div>
                  <label htmlFor="discussionContent" className="block text-sm font-medium text-gray-700 mb-2">
                    Discussion Content *
                  </label>
                  <textarea
                    id="discussionContent"
                    value={newDiscussion.content}
                    onChange={(e) => setNewDiscussion(prev => ({ ...prev, content: e.target.value }))}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue focus:border-apn-blue"
                    placeholder="Share your thoughts, ask questions, or start a conversation..."
                    required
                    maxLength={2000}
                  />
                  <p className="text-sm text-gray-500 mt-1">{newDiscussion.content.length}/2000 characters</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Discussion Guidelines:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Keep discussions professional and respectful</li>
                    <li>• Stay on topic and relevant to the group</li>
                    <li>• No spam, self-promotion, or inappropriate content</li>
                    <li>• Encourage constructive dialogue and networking</li>
                  </ul>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCancelDiscussion}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingDiscussion || !newDiscussion.title.trim() || !newDiscussion.content.trim()}
                    className="px-6 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                  >
                    {submittingDiscussion ? 'Creating...' : 'Start Discussion'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupDetail;
